<template>
  <div v-if="!levelStore.loading" class="mb-5 md:mb-15">
    <div v-if="loading" class="py-6">
      <CommonSkeleton :length="5" item-height="3rem" class="mb-2" />
    </div>

    <template v-else>
      <h1 class="text-center">{{ $t('exchange.title') }}</h1>

      <p class="md:text-lg mt-8">
        {{ $t('exchange.subtitle') }}
      </p>

      <div class="w-full">
        <div class="mt-10 flex">
          <div class="me-4">
            <label class="text-sm text-white block mb-2">{{
              $t('exchange.you_send')
            }}</label>
            <Dropdown
              v-model="fromCurrency"
              :options="currencies"
              optionLabel="name"
              optionValue="value"
              placeholder="Currency"
              class="w-30"
            />
          </div>
          <div class="w-full">
            <label class="block mb-2 flex">
              <span>
                {{ $t('exchange.your_balance') }}
                {{
                  fromCurrency === 'Tokens'
                    ? balance.totalTokens
                    : balance.totalCoins
                }}
              </span>
            </label>
            <InputGroup class="w-full">
              <InputText v-model="from" :placeholder="$t('app.amount')" />
              <Button severity="secondary" label="MAX" @click="setMaxAmount" />
            </InputGroup>
            <div class="flex items-center">
              <label
                v-if="userLevel?.exchange_commission"
                class="text-red-500 mt-2"
                >{{ $t('deposits.commission') }}:
                {{ userLevel.exchange_commission }}%</label
              >
              <span class="text-gray-400 ms-2">
                <Button
                  v-tooltip.focus.top="$t('exchange.commission_tooltip')"
                  tooltipEvent="focus"
                  class="p-0 text-gray-400 border-black bg-black"
                >
                  <Icon icon="fa-solid fa-info-circle"
                /></Button>
              </span>
            </div>
          </div>
        </div>

        <div class="flex justify-center">
          <div class="flex items-center mt-10">
            <Button @click="handleInterchange" rounded severity="secondary"
              ><Icon icon="fa-solid fa-retweet"
            /></Button>
          </div>
        </div>

        <div class="mt-10 flex">
          <div class="me-4">
            <label class="text-sm text-white block mb-2">{{
              $t('exchange.you_get')
            }}</label>
            <Dropdown
              v-model="toCurrency"
              :options="currencies"
              optionLabel="name"
              optionValue="value"
              placeholder="Currency"
              class="w-30"
            />
          </div>

          <div class="w-full">
            <label class="block mb-2">
              1 {{ fromCurrency }} ≈ {{ oneExchange }}
              {{ toCurrency }}
            </label>
            <InputText
              v-model="to"
              class="w-full"
              :placeholder="$t('app.amount')"
              disabled
            />
          </div>
        </div>

        <div class="w-full flex flex-col items-center mt-6">
          <CommonCaptchaWrapper
            v-if="website.sitekey || website.cf_turnstile_sitekey"
            @verify="handleCaptchaVerified"
            @error="handleCaptchaError"
            @expire="handleCaptchaExpired"
            :sitekey="website.sitekey"
            ref="myCaptcha"
            size="normal"
          />
        </div>

        <div class="w-full flex justify-center">
          <Button
            @click="handleRedeem"
            class="mt-8 w-1/2 flex justify-center"
            :disabled="!canRedeem"
          >
            {{ $t('exchange.confirm') }}
          </Button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeMount, nextTick } from 'vue';
import { useWebsiteInfoStore } from '@/stores/website';
import { useUserStore } from '@/stores/user';
import { useToast } from 'primevue/usetoast';

const websiteInfoStore = useWebsiteInfoStore();
const levelStore = useLevelStore();

const website = computed(() => websiteInfoStore.website);
const api = useApi();
const userStore = useUserStore();
const toast = useToast();
const { t } = useI18n();

const levels = computed(() => levelStore.levels);

const user = computed(() => userStore.user);

const userLevel = computed(() =>
  levels.value.find((v) =>
    user.value.user_level
      ? v.id === user.value.user_level.level_id
      : v.earning_requirement === 0
  )
);

const balance = ref({
  totalTokens: 0,
  totalUsdTokens: 0,
  totalCoins: 0,
});
const loading = ref(true);
const isRedeeming = ref(false);
const from = ref(0);
const to = ref(0);
const currencies = ref([]);
const fromCurrency = ref(null);
const toCurrency = ref(null);
const oneExchange = ref(null);
const myCaptcha = ref(null);
const captchaToken = ref(null);

const canRedeem = computed(() => {
  if (loading.value || isRedeeming.value) return false;
  if (!from.value || from.value <= 0) return false;
  return !!captchaToken.value;
});

watch(fromCurrency, (newValue) => {
  if (newValue === 'Tokens') {
    toCurrency.value = website.value.currency;
  } else {
    toCurrency.value = 'Tokens';
  }
  oneExchange.value = convertCurrency(1, fromCurrency.value);
  updateTo(from.value);
});

watch(from, (newValue) => {
  updateTo(newValue);
});

onBeforeMount(async () => {
  await levelStore.fetchLevels();
});

onMounted(async () => {
  const response = await api.execute('/api/tokens-balance');
  balance.value.totalTokens = response.total_tokens;
  balance.value.totalUsdTokens = response.usd_tokens;
  balance.value.totalCoins = response.total_coins;
  userStore.updateUser({
    total_tokens: response.total_tokens,
    total_coins: response.total_coins,
  });

  currencies.value.push(
    { name: website.value.currency, value: website.value.currency },
    { name: 'Tokens', value: 'Tokens' }
  );

  fromCurrency.value = 'Tokens';
  loading.value = false;
});

const handleInterchange = () => {
  let temp;
  temp = fromCurrency.value;
  fromCurrency.value = toCurrency.value;
  toCurrency.value = temp;
};

const updateTo = (num) => {
  to.value = convertCurrency(parseFloat(num), fromCurrency.value);
};

const convertCurrency = (amount, fromCurrency) => {
  const tokensPerDollar = 500; // 500 tokens is 1 dollar

  let result = 0;
  let commission = (userLevel.value?.exchange_commission || 0) / 100;

  if (fromCurrency === 'Tokens') {
    const dollars = amount / tokensPerDollar;
    result = dollars / website.value.exchange_rate;
    result = result * (1 - commission);
  } else {
    const dollars = amount * website.value.exchange_rate;
    result = dollars * tokensPerDollar;
    result = result * (1 - commission);
  }

  if (isNaN(result)) {
    result = 0;
  }

  return formatAmount(result);
};

const handleCaptchaVerified = (token) => {
  captchaToken.value = token;
};

const handleCaptchaExpired = () => {
  captchaToken.value = null;
};

const handleRedeem = async () => {
  if (!canRedeem.value) return;

  await executeRedeem();
};

const executeRedeem = async () => {
  isRedeeming.value = true;

  const captchaParam =
    website.value.captcha_provider === 'hcaptcha'
      ? 'hcaptcha'
      : 'cf-turnstile-response';

  const amount = from.value;

  try {
    const response = await api.execute('/api/exchange', 'POST', {
      amount: amount,
      currency: fromCurrency.value,
      [captchaParam]: captchaToken.value,
    });

    captchaToken.value = null;

    balance.value.totalTokens = response.balance_tokens;
    balance.value.totalUsdTokens = response.usd_tokens;
    balance.value.totalCoins = response.balance_total_coins;
    userStore.updateUser({
      total_tokens: response.total_tokens,
      total_coins: response.balance_total_coins,
    });

    toast.add({
      severity: 'success',
      summary: t('exchange.toast_title'),
      detail: t('exchange.toast_description', {
        from: `${from.value} ${fromCurrency.value}`,
        to: `${to.value} ${toCurrency.value}`,
      }),
      life: 2000,
    });

    from.value = 0;
    to.value = 0;
  } catch (error) {
    captchaToken.value = null;
  } finally {
    isRedeeming.value = false;
  }
};

const setMaxAmount = () => {
  from.value =
    fromCurrency.value === 'Tokens'
      ? balance.value.totalTokens
      : balance.value.totalCoins;
};

const handleCaptchaError = () => {
  captchaToken.value = null;
  toast.add({
    severity: 'error',
    summary: 'Error',
    detail: t('free.captcha_error'),
    life: 5000,
  });
};
</script>
